import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

import type { SkuDisplayStatus } from '../utils/sku-status';

/**
 * One status marker for a product / SKU row (rules in `../utils/sku-status`).
 * Three treatments only: the amber Draft chip (also the PAP view's draft
 * chip, ProductSyncRow), the emerald Active pill (DESIGN.md §13.1), and quiet
 * grey text for the three "not live" states. null renders nothing: an
 * unknown status never gets a guessed badge.
 */
export function SkuStatusBadge({
  status,
  className,
}: {
  status: SkuDisplayStatus | null;
  className?: string;
}) {
  const { t } = useTranslation('inventory');
  if (status === null) return null;

  const pill = 'shrink-0 rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1';
  const greyText = 'shrink-0 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground';

  switch (status) {
    case 'draft':
      return (
        <span className={cn(pill, 'bg-amber-100 text-amber-800 ring-amber-300/80', className)}>
          {t('products.draft')}
        </span>
      );
    case 'active':
      return (
        <span className={cn(pill, 'bg-emerald-100 text-emerald-800 ring-emerald-300/80', className)}>
          {t('products.statusActive')}
        </span>
      );
    case 'archived':
      return (
        <span className={cn(greyText, className)}>{t('products.statusArchived')}</span>
      );
    case 'archived_on_shopify':
      return (
        <span className={cn(greyText, className)} title={t('products.statusNotOnShopifyTitle')}>
          {t('products.statusArchivedOnShopify')}
        </span>
      );
    case 'deleted_on_shopify':
      return (
        <span className={cn(greyText, className)} title={t('products.statusNotOnShopifyTitle')}>
          {t('products.statusDeletedOnShopify')}
        </span>
      );
    default:
      return null;
  }
}
